/**
 * @author      shivani mehra
 * @date        June, 2026
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from "react";
import { Container, Card, Row, Col, Form, Button, Table, Badge, Alert, Spinner } from "react-bootstrap";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jwt_decode from "jwt-decode";
import { FaCopy, FaCheck, FaLink, FaPlug, FaSave, FaSync } from "react-icons/fa";
import * as constants from "../../constants/CONSTANT";

const ZohoSetting = () => {
  // Credentials State
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [redirectUri, setRedirectUri] = useState("");
  const [apiDomain, setApiDomain] = useState("https://www.zohoapis.in");
  const [accountsDomain, setAccountsDomain] = useState("https://accounts.zoho.in");
  const [isActive, setIsActive] = useState(true);

  // Connection & Integration state
  const [settingRecord, setSettingRecord] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [zohoFields, setZohoFields] = useState([]);
  const [isFieldsLoading, setIsFieldsLoading] = useState(false);

  // Business numbers state
  const [businessNumbers, setBusinessNumbers] = useState([]);
  const [selectedBusinessNumber, setSelectedBusinessNumber] = useState("");

  // Field Mapping State
  const [selectedModule, setSelectedModule] = useState("Contacts");
  const [uniqueField, setUniqueField] = useState("whatsapp_number");
  const [uiMappings, setUiMappings] = useState({
    firstname: { type: "none", value: "" },
    lastname: { type: "none", value: "" },
    email: { type: "none", value: "" },
    whatsapp_number: { type: "none", value: "" },
    title: { type: "none", value: "" },
    leadsource: { type: "none", value: "" },
    leadstatus: { type: "none", value: "" },
    address: { type: "none", value: "" },
    description: { type: "none", value: "" },
    dob: { type: "none", value: "" },
    tags: { type: "none", value: "" }
  });

  const [leadStatusOptions] = useState(() => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem("lead_status"));
      if (Array.isArray(parsed)) {
        return parsed.map(item => typeof item === "string" ? item : (item.label || item.value || ""));
      }
    } catch (e) {
      console.error("Error reading lead status from session storage:", e);
    }
    return ["Open - Not Contacted", "Working Contacted", "Closed - Converted", "Closed - Not Converted"];
  });

  const leadSourceOptions = [
    "Phone",
    "Partner Referral",
    "BNI",
    "Purchased List",
    "Web",
    "Email",
    "Whatsapp",
    "Ad Leads",
    "Salesforce",
    "Zoho",
    "Other"
  ];

  const [zohoModules, setZohoModules] = useState([]);
  const [isModulesLoading, setIsModulesLoading] = useState(false);
  const [activeMappings, setActiveMappings] = useState([]);
  const [isMappingsLoading, setIsMappingsLoading] = useState(false);

  // UI state
  const [isSavingSetting, setIsSavingSetting] = useState(false);
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [copiedPayload, setCopiedPayload] = useState(false);


  const [userInfo] = useState(() => {
    try {
      return jwt_decode(sessionStorage.getItem("token"));
    } catch (e) {
      return {};
    }
  });

  // Helper: Default redirect URI
  const defaultRedirectUri = `${window.location.protocol}//${window.location.host}/zoho-integration`;

  useEffect(() => {
    fetchZohoSettings();
    fetchWhatsAppSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (businessNumbers.length > 0 && !selectedBusinessNumber) {
      setSelectedBusinessNumber(businessNumbers[0].phone);
    }
  }, [businessNumbers, selectedBusinessNumber]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchZohoFieldsList(selectedModule);
      fetchFieldMappingList(selectedModule);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModule, isAuthenticated]);


  useEffect(() => {
    if (isAuthenticated) {
      fetchZohoModulesList();
      fetchActiveMappingsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const fetchWhatsAppSettings = async () => {
    try {
      const response = await WhatsAppAPI.getWhatsAppSettings();
      if (response && response.success && response.record) {
        setBusinessNumbers(response.record);
      }
    } catch (error) {
      console.error("Error fetching WhatsApp settings:", error);
    }
  };

  const fetchZohoModulesList = async () => {
    setIsModulesLoading(true);
    try {
      const response = await WhatsAppAPI.getZohoModules();
      if (response.success && response.modules) {
        setZohoModules(response.modules);
        if (response.modules.length > 0 && !selectedModule) {
          const hasContacts = response.modules.some(m => m.api_name === "Contacts");
          const hasLeads = response.modules.some(m => m.api_name === "Leads");
          if (hasContacts) setSelectedModule("Contacts");
          else if (hasLeads) setSelectedModule("Leads");
          else setSelectedModule(response.modules[0].api_name);
        }
      }
    } catch (error) {
      console.error("Error fetching Zoho modules:", error);
    } finally {
      setIsModulesLoading(false);
    }
  };

  const fetchActiveMappingsList = async () => {
    setIsMappingsLoading(true);
    try {
      const response = await WhatsAppAPI.getZohoMappings();
      if (response.success && response.mappings) {
        setActiveMappings(response.mappings);
      }
    } catch (error) {
      console.error("Error fetching active mappings:", error);
    } finally {
      setIsMappingsLoading(false);
    }
  };

  const fetchZohoSettings = async () => {
    try {
      const response = await WhatsAppAPI.getZohoSetting();
      if (response.success && response.setting) {
        const setting = response.setting;
        setSettingRecord(setting);
        setClientId(setting.client_id || "");
        setClientSecret(setting.client_secret || "");
        setRedirectUri(setting.redirect_uri || defaultRedirectUri);
        setApiDomain(setting.api_domain || "https://www.zohoapis.in");
        setAccountsDomain(setting.accounts_domain || "https://accounts.zoho.in");
        setIsActive(setting.is_active !== undefined ? setting.is_active : true);

        // Check if authenticated
        if (setting.refresh_token) {
          setIsAuthenticated(true);
        }
      } else {
        setRedirectUri(defaultRedirectUri);
      }
    } catch (error) {
      console.error("Error fetching Zoho settings:", error);
      toast.error("Failed to fetch Zoho settings.");
    }
  };

  const fetchZohoFieldsList = async (moduleName = "Contacts") => {
    setIsFieldsLoading(true);
    try {
      const response = await WhatsAppAPI.getZohoFields(moduleName);
      if (response.success && response.fields) {
        setZohoFields(response.fields);
      } else {
        toast.warning(`Could not load Zoho CRM ${moduleName} fields.`);
      }
    } catch (error) {
      console.error("Error fetching Zoho fields:", error);
    } finally {
      setIsFieldsLoading(false);
    }
  };

  const convertDbToUiMappings = (mappingObj) => {
    const defaultMappings = {
      firstname: { type: "none", value: "" },
      lastname: { type: "none", value: "" },
      email: { type: "none", value: "" },
      whatsapp_number: { type: "none", value: "" },
      title: { type: "none", value: "" },
      leadsource: { type: "none", value: "" },
      leadstatus: { type: "none", value: "" },
      address: { type: "none", value: "" },
      description: { type: "none", value: "" },
      dob: { type: "none", value: "" },
      tags: { type: "none", value: "" }
    };

    if (!mappingObj || typeof mappingObj !== "object") return defaultMappings;

    const result = { ...defaultMappings };

    Object.keys(defaultMappings).forEach((localField) => {
      // 1. Check if there is an incoming static mapping (e.g. __static_leadsource)
      const staticKey = `__static_${localField}`;
      if (mappingObj[staticKey] !== undefined) {
        result[localField] = {
          type: "static",
          value: mappingObj[staticKey]
        };
        return;
      }

      // 2. Check if a Zoho field is mapped to this local field
      const mappedZohoField = Object.keys(mappingObj).find((zohoKey) => {
        if (zohoKey === "__business_number" || zohoKey.startsWith("__static_")) return false;
        const val = mappingObj[zohoKey];
        return val === localField || (typeof val === "string" && val.startsWith("static:") && val.substring(7) === localField);
      });

      if (mappedZohoField) {
        const val = mappingObj[mappedZohoField];
        if (typeof val === "string" && val.startsWith("static:")) {
          result[localField] = {
            type: "static",
            value: val.substring(7)
          };
        } else {
          result[localField] = {
            type: "dynamic",
            value: mappedZohoField
          };
        }
      }
    });

    return result;
  };

  const getDefaultUiMappings = (moduleName) => {
    if (moduleName === "Leads") {
      return {
        firstname: { type: "dynamic", value: "First_Name" },
        lastname: { type: "dynamic", value: "Last_Name" },
        email: { type: "dynamic", value: "Email" },
        whatsapp_number: { type: "dynamic", value: "Phone" },
        title: { type: "dynamic", value: "Title" },
        leadsource: { type: "static", value: "Zoho" },
        leadstatus: { type: "static", value: "Open - Not Contacted" },
        address: { type: "none", value: "" },
        description: { type: "dynamic", value: "Description" },
        dob: { type: "none", value: "" },
        tags: { type: "none", value: "" }
      };
    } else {
      // Contacts module
      return {
        firstname: { type: "dynamic", value: "First_Name" },
        lastname: { type: "dynamic", value: "Last_Name" },
        email: { type: "dynamic", value: "Email" },
        whatsapp_number: { type: "dynamic", value: "Phone" },
        title: { type: "dynamic", value: "Title" },
        leadsource: { type: "static", value: "Zoho" },
        leadstatus: { type: "static", value: "Open - Not Contacted" },
        address: { type: "dynamic", value: "Mailing_Street" },
        description: { type: "dynamic", value: "Description" },
        dob: { type: "none", value: "" },
        tags: { type: "none", value: "" }
      };
    }
  };

  const convertUiToDbMapping = (mappings) => {
    const dbMapping = {};
    
    Object.entries(mappings).forEach(([localField, config]) => {
      const { type, value } = config;
      if (type === "dynamic" && value) {
        dbMapping[value] = localField;
      } else if (type === "static") {
        dbMapping[`__static_${localField}`] = value;
      }
    });

    return dbMapping;
  };

  const fetchFieldMappingList = async (moduleName = "Contacts") => {
    try {
      const response = await WhatsAppAPI.getZohoMapping(moduleName);
      if (response.success && response.mapping) {
        const mappingObj = response.mapping.mapping; // JSON object { Zoho_API: Local_API }
        if (mappingObj && Object.keys(mappingObj).length > 0) {
          if (mappingObj.__business_number) {
            setSelectedBusinessNumber(mappingObj.__business_number);
            delete mappingObj.__business_number;
          }
          const parsed = convertDbToUiMappings(mappingObj);
          setUiMappings(parsed);
        } else {
          setUiMappings(getDefaultUiMappings(moduleName));
        }
        if (response.mapping.unique_field) {
          setUniqueField(response.mapping.unique_field);
        }
      } else {
        setUiMappings(getDefaultUiMappings(moduleName));
        setUniqueField("whatsapp_number");
      }
    } catch (error) {
      console.error("Error fetching mapping:", error);
      setUiMappings(getDefaultUiMappings(moduleName));
    }
  };

  const handleSaveSetting = async (e) => {
    if (e) e.preventDefault();
    if (!clientId || !clientSecret || !redirectUri) {
      toast.error("Client ID, Client Secret, and Redirect URI are required.");
      return;
    }

    setIsSavingSetting(true);
    try {
      const payload = {
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: redirectUri,
        api_domain: apiDomain,
        accounts_domain: accountsDomain,
        is_active: isActive
      };
      const response = await WhatsAppAPI.saveZohoSetting(payload);
      if (response.success) {
        toast.success("Zoho settings saved successfully.");
        setSettingRecord(response.setting);
      } else {
        toast.error("Failed to save settings: " + (response.errors || "Unknown error"));
      }
    } catch (error) {
      console.error("Error saving Zoho settings:", error);
      toast.error("Error saving settings.");
    } finally {
      setIsSavingSetting(false);
    }
  };

  const handleConnectZoho = async () => {
    // Save settings first before starting OAuth redirect
    await handleSaveSetting();

    try {
      toast.info("Generating Zoho Authorization URL...");
      const response = await WhatsAppAPI.getZohoAuthUrl();
      if (response.success && response.authUrl) {
        // Redirect the user directly to Zoho Consent Page
        window.location.href = response.authUrl;
      } else {
        toast.error("Failed to generate Auth URL: " + (response.errors || "Unknown error"));
      }
    } catch (error) {
      console.error("Error generating Auth URL:", error);
      toast.error("Connection error.");
    }
  };

  const handleSaveMapping = async () => {
    if (!settingRecord) {
      toast.error("Please configure and save credentials first.");
      return;
    }

    setIsSavingMapping(true);
    try {
      const dbMapping = convertUiToDbMapping(uiMappings);
      const payload = {
        zoho_setting_id: settingRecord.id,
        module_name: selectedModule,
        mapping: {
          ...dbMapping,
          __business_number: selectedBusinessNumber
        },
        unique_field: uniqueField
      };
      const response = await WhatsAppAPI.saveZohoMapping(payload);
      if (response.success) {
        toast.success("Field mapping saved successfully.");
        fetchActiveMappingsList();
      } else {
        toast.error("Failed to save mapping: " + (response.errors || "Unknown error"));
      }
    } catch (error) {
      console.error("Error saving Zoho mapping:", error);
      toast.error("Error saving mapping.");
    } finally {
      setIsSavingMapping(false);
    }
  };

  const handleUiMappingChange = (localField, fieldName, val) => {
    setUiMappings((prev) => ({
      ...prev,
      [localField]: {
        ...prev[localField],
        [fieldName]: val
      }
    }));
  };


  /* eslint-disable no-template-curly-in-string */
  const getWebhookPayloadTemplate = () => {
    const isLeads = selectedModule === "Leads";
    const isContacts = selectedModule === "Contacts";
    
    if (isContacts) {
      return JSON.stringify({
        id: "${Contacts.Contact Id}",
        First_Name: "${Contacts.First Name}",
        Last_Name: "${Contacts.Last Name}",
        Email: "${Contacts.Email}",
        Title: "${Contacts.Title}",
        Phone: "${Contacts.Phone}",
        Mobile: "${Contacts.Mobile}",
        Address: "${Contacts.Mailing Street}, ${Contacts.Mailing City}, ${Contacts.Mailing State}, ${Contacts.Mailing Zip}, ${Contacts.Mailing Country}",
        Description: "${Contacts.Description}",
        Tag: "${Contacts.Tag}"
      }, null, 2);
    }
    
    if (isLeads) {
      return JSON.stringify({
        id: "${Leads.Lead Id}",
        First_Name: "${Leads.First Name}",
        Last_Name: "${Leads.Last Name}",
        Email: "${Leads.Email}",
        Title: "${Leads.Title}",
        Phone: "${Leads.Phone}",
        Mobile: "${Leads.Mobile}",
        Address: "${Leads.Street}, ${Leads.City}, ${Leads.State}, ${Leads.Zip Code}, ${Leads.Country}",
        Description: "${Leads.Description}",
        Tag: "${Leads.Tag}"
      }, null, 2);
    }

    let singularModule = selectedModule;
    if (selectedModule.endsWith("s")) {
      singularModule = selectedModule.slice(0, -1);
    }
    return JSON.stringify({
      id: `\${${selectedModule}.${singularModule} Id}`,
      First_Name: `\${${selectedModule}.First Name}`,
      Last_Name: `\${${selectedModule}.Last Name}`,
      Email: `\${${selectedModule}.Email}`,
      Title: `\${${selectedModule}.Title}`,
      Phone: `\${${selectedModule}.Phone}`,
      Mobile: `\${${selectedModule}.Mobile}`,
      Description: `\${${selectedModule}.Description}`,
      Tag: `\${${selectedModule}.Tag}`
    }, null, 2);
  };
  /* eslint-enable no-template-curly-in-string */

  const copyPayloadToClipboard = () => {
    navigator.clipboard.writeText(getWebhookPayloadTemplate());
    setCopiedPayload(true);
    setTimeout(() => setCopiedPayload(false), 2000);
    toast.success("Webhook JSON Payload template copied!");
  };


  const getWebhookUrl = () => {
    const apiBase = constants.API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:3001`;
    return `${apiBase}/api/zoho/webhook?tenant=${userInfo?.tenantcode || "tenantcode"}&module=${selectedModule}`;
  };

  const copyWebhookToClipboard = () => {
    navigator.clipboard.writeText(getWebhookUrl());
    setCopiedWebhook(true);
    setTimeout(() => setCopiedWebhook(false), 2000);
    toast.success("Webhook URL copied to clipboard!");
  };



  return (
    <>
      <Container className="mt-5 pb-5">
        {/* Header Ribbon */}
        <Row className="mx-5 mb-4">
          <Card className="p-4 header-bg shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
            <small>
              Connect and synchronize your leads between WatConnect and Zoho CRM. Configure Zoho Client credentials, authenticate through Zoho OAuth, and establish custom mappings for fields.
            </small>
          </Card>
        </Row>

        <Row className="mx-5">
          {/* Left Column: Credentials & Status */}
          <Col lg={5} className="mb-4">
            <Card className="shadow-sm border-0 rounded-3 mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="fw-semibold mb-0">Connection Details</h6>
                  <Badge bg={isAuthenticated ? "success" : "danger"} className="p-2">
                    {isAuthenticated ? "Connected" : "Disconnected"}
                  </Badge>
                </div>

                <Form onSubmit={handleSaveSetting}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Zoho Datacenter Region</Form.Label>
                    <Form.Select
                      value={apiDomain}
                      onChange={(e) => {
                        const val = e.target.value;
                        setApiDomain(val);
                        if (val.includes(".in")) setAccountsDomain("https://accounts.zoho.in");
                        else if (val.includes(".com")) setAccountsDomain("https://accounts.zoho.com");
                        else if (val.includes(".eu")) setAccountsDomain("https://accounts.zoho.eu");
                        else if (val.includes(".au")) setAccountsDomain("https://accounts.zoho.com.au");
                      }}
                    >
                      <option value="https://www.zohoapis.in">India (.in)</option>
                      <option value="https://www.zohoapis.com">US / International (.com)</option>
                      <option value="https://www.zohoapis.eu">Europe (.eu)</option>
                      <option value="https://www.zohoapis.com.au">Australia (.com.au)</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Client ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 1000.XXXXX"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Client Secret</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Your client secret token"
                      value={clientSecret}
                      onChange={(e) => setClientSecret(e.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Authorized Redirect URI</Form.Label>
                    <Form.Control
                      type="text"
                      value={redirectUri}
                      onChange={(e) => setRedirectUri(e.target.value)}
                    />
                    <Form.Text className="text-muted small block">
                      Register this URL in Zoho Developer Console under Authorized Redirect URIs.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                      type="switch"
                      id="active-switch"
                      label="Enable Zoho Integration Sync"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="fw-semibold text-secondary"
                    />
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button variant="primary" type="submit" disabled={isSavingSetting}>
                      {isSavingSetting ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" /> Save Credentials</>}
                    </Button>
                    <Button variant="outline-success" onClick={handleConnectZoho} className="d-flex align-items-center justify-content-center">
                      <FaSync className="me-2" /> Connect/Re-authorize Zoho
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Webhook Configuration Card */}
            <Card className="shadow-sm border-0 rounded-3 mb-4">
              <Card.Body className="p-4">
                <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                  <FaLink /> Zoho CRM Webhook Setup
                </h6>
                <p className="text-muted small mb-2">
                  To sync leads from Zoho CRM back to WatConnect instantly, create a webhook in Zoho CRM Settings pointing to the URL below:
                </p>
                <div className="bg-light p-2 rounded border d-flex align-items-center justify-content-between mb-3">
                  <code className="text-truncate me-2" style={{ fontSize: "0.85rem" }}>{getWebhookUrl()}</code>
                  <Button variant="link" className="p-0 text-primary" onClick={copyWebhookToClipboard}>
                    {copiedWebhook ? <FaCheck className="text-success" /> : <FaCopy />}
                  </Button>
                </div>

                <div className="mb-3 bg-light p-2 rounded border">
                  <span className="text-secondary small fw-bold d-block mb-1">Webhook Configuration Details:</span>
                  <ul className="text-muted small ps-3 mb-0" style={{ fontSize: "0.85rem", listStyleType: "square" }}>
                    <li><strong>Method:</strong> POST</li>
                    <li><strong>Authorization Type:</strong> General</li>
                    <li><strong>Module:</strong> {selectedModule}</li>
                  </ul>
                </div>

                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span className="text-secondary small fw-bold">Raw JSON Body Payload:</span>
                    <Button variant="link" size="sm" className="p-0 text-primary small d-flex align-items-center gap-1 text-decoration-none" onClick={copyPayloadToClipboard}>
                      {copiedPayload ? <><FaCheck className="text-success" /> Copied!</> : <><FaCopy /> Copy Payload</>}
                    </Button>
                  </div>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    readOnly
                    value={getWebhookPayloadTemplate()}
                    className="bg-light text-monospace small"
                    style={{ fontFamily: "monospace", fontSize: "0.8rem", resize: "none" }}
                  />
                </div>

                <Alert variant="info" className="small py-2 mb-0 border-0">
                  Select <strong>{selectedModule} module</strong> trigger on <strong>Create/Edit</strong> in Zoho CRM workflow webhook panel. In the Body section, select <strong>Raw</strong> &gt; <strong>JSON</strong> format, and paste the payload template above.
                </Alert>
              </Card.Body>
            </Card>


            {/* Active Module Mappings Card */}
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body className="p-4">
                <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                  <FaCheck className="text-success" /> Active Module Mappings
                </h6>
                <p className="text-muted small">
                  WatConnect leads will automatically sync to all active module mappings below:
                </p>
                {isMappingsLoading ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" variant="success" />
                  </div>
                ) : activeMappings.length === 0 ? (
                  <Alert variant="warning" className="small py-2 mb-0 border-0">
                    No active module mappings configured.
                  </Alert>
                ) : (
                  <Table responsive size="sm" className="align-middle mb-0" style={{ fontSize: "0.85rem" }}>
                    <thead>
                      <tr>
                        <th>Module</th>
                        <th>Unique Field</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeMappings.map((mapping) => (
                        <tr key={mapping.id}>
                          <td className="fw-medium text-primary">{mapping.module_name}</td>
                          <td className="text-muted">{mapping.unique_field}</td>
                          <td className="text-end">
                            <Button
                              variant="link"
                              className="p-0 text-danger text-decoration-none"
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to deactivate mapping for ${mapping.module_name}?`)) {
                                  try {
                                    const res = await WhatsAppAPI.deleteZohoMapping(mapping.id);
                                    if (res.success) {
                                      toast.success(`Mapping for ${mapping.module_name} deactivated successfully.`);
                                      fetchActiveMappingsList();
                                    } else {
                                      toast.error("Failed to delete mapping: " + (res.errors || "Unknown error"));
                                    }
                                  } catch (err) {
                                    console.error("Error deleting mapping:", err);
                                    toast.error("Error deleting mapping.");
                                  }
                                }
                              }}
                            >
                              Deactivate
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column: Unique Matching Field & Field Mapping Grid */}
          <Col lg={7} className="mb-4">
            <Card className="shadow-sm border-0 rounded-3">
              <Card.Body className="p-4">
                <h6 className="fw-semibold mb-2">Unique matching field & Field Mapping</h6>
                <p className="text-muted small mb-4">
                  Define which field represents a unique lead to identify duplicates during updates, and map your Zoho CRM Fields ({selectedModule}) to watconnect local variables.
                </p>

                {/* Deduplication Match Criteria & Default Business Number */}
                <div className="bg-light p-3 rounded mb-4">
                  <Row>
                    <Col md={4}>
                      <h6 className="fw-bold mb-2 small text-uppercase text-secondary">Zoho CRM Module</h6>
                      <Form.Group className="mb-0">
                        <Form.Select
                          value={selectedModule}
                          onChange={(e) => setSelectedModule(e.target.value)}
                          style={{ fontWeight: "500" }}
                          disabled={isModulesLoading}
                        >
                          {zohoModules.length === 0 ? (
                            <>
                              <option value="Contacts">Contacts</option>
                              <option value="Leads">Leads</option>
                            </>
                          ) : (
                            zohoModules.map((m) => (
                              <option key={m.api_name} value={m.api_name}>
                                {m.plural_label} ({m.api_name})
                              </option>
                            ))
                          )}
                        </Form.Select>
                        <Form.Text className="text-muted small block mt-1">
                          Choose the Zoho CRM module you want to sync with.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6 className="fw-bold mb-2 small text-uppercase text-secondary">Unique Lead Identifier Match</h6>
                      <Form.Group className="mb-0">
                        <Form.Select
                          value={uniqueField}
                          onChange={(e) => setUniqueField(e.target.value)}
                          style={{ fontWeight: "500" }}
                        >
                          <option value="whatsapp_number">📞 WhatsApp Phone Number (Recommended)</option>
                          <option value="email">✉️ Email Address</option>
                          <option value="zoho_id">🔑 Zoho Record ID</option>
                        </Form.Select>
                        <Form.Text className="text-muted small block mt-1">
                          Updates and de-duplications will be matched on this criteria.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <h6 className="fw-bold mb-2 small text-uppercase text-secondary">Default WhatsApp Number</h6>
                      <Form.Group className="mb-0">
                        <Form.Select
                          value={selectedBusinessNumber}
                          onChange={(e) => setSelectedBusinessNumber(e.target.value)}
                          style={{ fontWeight: "500" }}
                        >
                          {businessNumbers.map((num) => (
                            <option key={num.id} value={num.phone}>
                              {num.name} ({num.phone})
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted small block mt-1">
                          Contacts imported from Zoho CRM will be assigned to this number.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Dynamic Fields Mapping Panel */}

                <h6 className="fw-semibold small mb-3 text-secondary text-uppercase">Field Matrix ({selectedModule})</h6>

                {isFieldsLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted small mt-2">Fetching CRM fields meta...</p>
                  </div>
                ) : (
                  <>
                    <Table responsive hover className="align-middle border-0 mb-4" style={{ fontSize: "0.9rem" }}>
                      <thead>
                        <tr className="bg-light border-0">
                          <th className="border-0" style={{ width: "30%" }}>watconnect CRM Field</th>
                          <th className="border-0" style={{ width: "30%" }}>Mapping Type</th>
                          <th className="border-0" style={{ width: "40%" }}>Zoho CRM Field / Static Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { value: "firstname", label: "First Name" },
                          { value: "lastname", label: "Last Name" },
                          { value: "email", label: "Email" },
                          { value: "title", label: "Title" },
                          { value: "whatsapp_number", label: "WhatsApp Number" },
                          { value: "leadsource", label: "Lead Source" },
                          { value: "leadstatus", label: "Lead Status" },
                          { value: "address", label: "Address" },
                          { value: "description", label: "Description" },
                          { value: "dob", label: "Date of Birth" },
                          { value: "tags", label: "Tags" }
                        ].map((field) => {
                          const localKey = field.value;
                          const currentMapping = uiMappings[localKey] || { type: "none", value: "" };
                          const isSourceOrStatus = localKey === "leadsource" || localKey === "leadstatus";

                          return (
                            <tr key={localKey}>
                              <td className="fw-semibold text-secondary">
                                {field.label}
                              </td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={currentMapping.type}
                                  onChange={(e) => {
                                    const newType = e.target.value;
                                    handleUiMappingChange(localKey, "type", newType);
                                    handleUiMappingChange(localKey, "value", "");
                                  }}
                                  style={{ maxWidth: "200px" }}
                                >
                                  <option value="none">-- Unmapped --</option>
                                  {!isSourceOrStatus && (
                                    <option value="dynamic">🔗 Map to Zoho Field</option>
                                  )}
                                  <option value="static">➕ Static Value</option>
                                </Form.Select>
                              </td>
                              <td>
                                {currentMapping.type === "dynamic" && !isSourceOrStatus && (
                                  <Form.Select
                                    size="sm"
                                    value={currentMapping.value}
                                    onChange={(e) => handleUiMappingChange(localKey, "value", e.target.value)}
                                  >
                                    <option value="">-- Select Zoho CRM Field --</option>
                                    {zohoFields.map((f) => (
                                      <option key={f.api_name} value={f.api_name}>
                                        {f.display_label} ({f.api_name})
                                      </option>
                                    ))}
                                  </Form.Select>
                                )}

                                {currentMapping.type === "static" && (
                                  <>
                                    {localKey === "leadsource" ? (
                                      <Form.Select
                                        size="sm"
                                        value={currentMapping.value}
                                        onChange={(e) => handleUiMappingChange(localKey, "value", e.target.value)}
                                      >
                                        <option value="">-- Select Lead Source --</option>
                                        {leadSourceOptions.map(opt => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </Form.Select>
                                    ) : localKey === "leadstatus" ? (
                                      <Form.Select
                                        size="sm"
                                        value={currentMapping.value}
                                        onChange={(e) => handleUiMappingChange(localKey, "value", e.target.value)}
                                      >
                                        <option value="">-- Select Lead Status --</option>
                                        {leadStatusOptions.map(opt => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </Form.Select>
                                    ) : (
                                      <Form.Control
                                        type="text"
                                        size="sm"
                                        placeholder={`Enter static ${field.label.toLowerCase()}`}
                                        value={currentMapping.value}
                                        onChange={(e) => handleUiMappingChange(localKey, "value", e.target.value)}
                                      />
                                    )}
                                  </>
                                )}

                                {currentMapping.type === "none" && (
                                  <span className="text-muted small">Not synchronized</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    <div className="d-grid gap-2">
                      <Button variant="success" onClick={handleSaveMapping} disabled={isSavingMapping}>
                        {isSavingMapping ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" /> Save Field Mappings</>}
                      </Button>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastContainer autoClose={3000} />
    </>
  );
};

export default ZohoSetting;
